import { Undefinable } from '@symfa-inc/providence-types';
import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import {
  DEFAULT_JURISDICTION_PERMITTING_PANEL_FIELDS,
  DEFAULT_JURISDICTION_ZONING_PANEL_FIELDS,
} from '@models/constants/default-jurisdiction-panel-fields';
import {
  FinancialData,
  JurisdictionBody,
  JurisdictionBrowse,
  JurisdictionDetails,
  JurisdictionDetailsContacts,
  JurisdictionDetailsPermitting,
  JurisdictionDetailsZoning,
  JurisdictionModalValues,
  JurisdictionOption,
  JurisdictionPermittingData,
  JurisdictionZoningData,
  MainBrowseFilter,
  OptionProps,
  PaginatedResponse,
  PermittingBrowse,
  PermittingBrowseFilter,
  PermittingJurisdictionAuditBody,
  PermittingJurisdictionAuditData,
  PermittingSofData,
  PermittingSummaryData,
  PermittingWorkflowBody,
  PermittingWorkflowData,
  ProjectCategory,
  ProjectDetails,
} from '@models/interfaces';
import {
  BreadcrumbsActions,
  FinancialActions,
  PermittingActions,
} from '@store/actions';
import {
  PermittingEffects,
  ProjectCategoriesEffects,
  ProjectsEffects,
} from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class PermittingHttpService extends CommonHttpService {
  @StoredRequest(PermittingActions.getPermittingBrowseData)
  getPermittingBrowseData(
    params: PermittingBrowseFilter,
  ): Promise<PaginatedResponse<PermittingBrowse>> {
    return this.http.callApi.get(`${this.apiUrl}/permitting`, null, { params });
  }

  async deleteJurisdiction(jurisdictionId: string): Promise<void> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions`,
      null,
      {
        params: { jurisdictionId },
      },
    );
  }

  @StoredRequest(PermittingActions.getJurisdictionList)
  getJurisdictionList(): Promise<JurisdictionOption[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/list-options`,
    );
  }

  @StoredRequest(PermittingActions.getPermittingUsersOptions)
  getPermittingUsersOptions(): Promise<OptionProps[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/permitting-users-options`,
    );
  }

  async getProjectCategoriesByBundleId(
    projectBundleID: string,
  ): Promise<ProjectCategory[]> {
    const response: ProjectCategory[] = await this.http.callApi.get(
      `${this.apiUrl}/projects/categories/${projectBundleID}`,
    );

    ProjectCategoriesEffects.setProjectCategories(response);

    return response;
  }

  @StoredRequest(
    PermittingActions.getSummaryTabData,
    BreadcrumbsActions.Permitting.permitting,
  )
  async getPermittingSummaryData(
    projectBundleID: string,
  ): Promise<PermittingSummaryData> {
    const response: PermittingSummaryData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/summary`,
    );

    ProjectsEffects.setHoldOptions({
      holdReason: response.project.holdReason,
      projectStatus: { id: response.project.projectStatus, value: '' },
    } as ProjectDetails);

    PermittingEffects.setCurrentProvidenceAgent(
      PermittingEffects.setPaceTaskFields(
        PermittingEffects.setPaceTasks(response),
      ),
    );

    return response;
  }

  @StoredRequest(PermittingActions.getWorkflowTabData)
  async getPermittingWorkflowData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<PermittingWorkflowData> {
    const response: PermittingWorkflowData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/workflow`,
    );

    return response;
  }

  @StoredRequest(PermittingActions.getJurisdictionAuditTabData)
  async getPermittingJurisdictionAuditData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<PermittingJurisdictionAuditData> {
    const response: PermittingJurisdictionAuditData =
      await this.http.callApi.get(
        `${this.apiUrl}/permitting/${projectBundleID}/jurisdiction-audit`,
      );

    return response;
  }

  @StoredRequest(PermittingActions.getContactsTabData)
  async getPermittingContactsData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<JurisdictionDetailsContacts[]> {
    const response: JurisdictionDetailsContacts[] = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/contacts`,
    );

    return response;
  }

  @StoredRequest(PermittingActions.getSofTabData)
  async getPermittingSofData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<PermittingSofData> {
    const response: PermittingSofData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/sof`,
    );

    if (response?.equipmentsByTypes) {
      PermittingEffects.setEquipmentsByTypes(response);
    }

    return response;
  }

  @StoredRequest(
    FinancialActions.getFinancialDataAction,
    PermittingActions.getFinancialTabData,
  )
  async getPermittingFinancialData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<FinancialData> {
    const response: FinancialData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/financial`,
    );

    return response;
  }

  @StoredRequest(PermittingActions.getPermittingDetailsData)
  async getPermittingDetailsData(
    projectBundleID: string,
  ): Promise<ProjectDetails> {
    const response = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}`,
    );

    ProjectsEffects.setHoldOptions({
      holdReason: response.holdReason,
      projectStatus: { id: response.projectStatus, value: '' },
    } as ProjectDetails);

    return PermittingEffects.setFinancial(
      ProjectsEffects.setCurrentProvidenceAgent(
        ProjectsEffects.setPaceTaskFields(
          ProjectsEffects.setPaceTasks(
            ProjectCategoriesEffects.setProjectCategoriesFromDetails(response),
          ),
        ),
      ),
    );
  }

  @StoredRequest(PermittingActions.permittingUpdateWorkflowData)
  async updatePermittingWorkflowData(
    projectId: string,
    body: PermittingWorkflowBody,
  ): Promise<PermittingWorkflowBody> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/${projectId}/workflow`,
      body,
    );

    return body;
  }

  @StoredRequest(PermittingActions.permittingUpdateJurisdictionAuditData)
  async updatePermittingJurisdictionAuditData(
    projectId: string,
    body: PermittingJurisdictionAuditBody,
  ): Promise<PermittingJurisdictionAuditBody> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/${projectId}/jurisdiction-audit`,
      body,
    );

    return body;
  }

  // PERMITTING - JURISDICTION
  @StoredRequest(PermittingActions.getJurisdictionBrowseData)
  getJurisdictionBrowseData(
    params: MainBrowseFilter,
  ): Promise<PaginatedResponse<JurisdictionBrowse>> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions`,
      null,
      { params },
    );
  }

  @StoredRequest(
    PermittingActions.getJurisdictionDetailsData,
    BreadcrumbsActions.Permitting.jurisdiction,
  )
  async getJurisdictionDetailsData(
    jurisdictionPath: string,
  ): Promise<JurisdictionDetails> {
    const url = `${this.apiUrl}/permitting/jurisdictions/${jurisdictionPath}`;
    const [zoning, permitting, contacts]: [
      Omit<JurisdictionDetails, 'permitting' | 'contacts'> | null,
      JurisdictionDetailsPermitting[] | null,
      JurisdictionDetailsContacts[] | null,
    ] = await Promise.all([
      this.http.callApi.get(`${url}/zoning`),
      this.http.callApi.get(`${url}/permitting`),
      this.http.callApi.get(`${url}/contacts`),
    ]);

    if (!zoning || !permitting || !contacts) {
      throw new Error(
        'Jurisdiction details are unavailable. Please reload the page.',
      );
    }

    return { ...zoning, permitting, contacts };
  }

  getJurisdictionNameValidation(
    id: Undefinable<string>,
    stateId: Undefinable<string>,
    jurisdictionName: string,
  ): Promise<boolean> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/validation`,
      null,
      {
        params: { jurisdictionName: jurisdictionName.trim(), id, stateId },
      },
    );
  }

  @StoredRequest(PermittingActions.addJurisdiction)
  addJurisdiction(
    params: MainBrowseFilter,
    body: JurisdictionBody,
  ): Promise<PaginatedResponse<JurisdictionBrowse>> {
    return this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions`,
      body,
      { params },
    );
  }

  @StoredRequest(PermittingActions.addJurisdictionZoningPanel)
  async addJurisdictionZoningPanel(
    jurisdictionId: string,
    body: Omit<JurisdictionModalValues, 'id'>,
  ): Promise<JurisdictionDetailsZoning> {
    const id = await this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/zoning-panel`,
      body,
    );

    return {
      id,
      ...body,
      ...DEFAULT_JURISDICTION_ZONING_PANEL_FIELDS,
    };
  }

  @StoredRequest(PermittingActions.updateJurisdictionZoning)
  async updateJurisdictionZoning(
    jurisdictionId: string,
    body: JurisdictionZoningData,
  ): Promise<JurisdictionZoningData> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/zoning`,
      body,
    );

    return body;
  }

  @StoredRequest(PermittingActions.updateJurisdictionZoningPanel)
  async updateJurisdictionZoningPanel(
    values: JurisdictionModalValues,
  ): Promise<JurisdictionModalValues> {
    const { id, ...body } = values;

    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/zoning-panel`,
      body,
      { params: { zoningPanelId: id } },
    );

    return values;
  }

  @StoredRequest(PermittingActions.removeJurisdictionZoningPanel)
  async removeJurisdictionZoningPanel(zoningPanelId: string): Promise<string> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions/zoning-panel`,
      null,
      { params: { zoningPanelId } },
    );

    return zoningPanelId;
  }

  @StoredRequest(PermittingActions.addJurisdictionPermittingPanel)
  async addJurisdictionPermittingPanel(
    jurisdictionId: string,
    body: Omit<JurisdictionModalValues, 'id'>,
  ): Promise<JurisdictionDetailsPermitting> {
    const id = await this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/permitting-panel`,
      body,
    );

    return {
      id,
      ...body,
      ...DEFAULT_JURISDICTION_PERMITTING_PANEL_FIELDS,
    };
  }

  @StoredRequest(PermittingActions.updateJurisdictionPermittingPanel)
  async updateJurisdictionPermittingPanel(
    values: JurisdictionModalValues,
  ): Promise<JurisdictionModalValues> {
    const { id, ...body } = values;

    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/permitting-panel`,
      body,
      { params: { permittingPanelId: id } },
    );

    return values;
  }

  @StoredRequest(PermittingActions.removeJurisdictionPermittingPanel)
  async removeJurisdictionPermittingPanel(
    permittingPanelId: string,
  ): Promise<string> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions/permitting-panel`,
      null,
      { params: { permittingPanelId } },
    );

    return permittingPanelId;
  }

  @StoredRequest(PermittingActions.updateJurisdictionPermitting)
  async updateJurisdictionPermitting(
    jurisdictionId: string,
    body: JurisdictionPermittingData,
  ): Promise<JurisdictionPermittingData> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/permitting`,
      body,
    );

    return body;
  }

  @StoredRequest(PermittingActions.addJurisdictionContact)
  async addJurisdictionContact(
    jurisdictionId: string,
    body: Omit<JurisdictionDetailsContacts, 'id'>,
  ): Promise<JurisdictionDetailsContacts> {
    const id = await this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions/contacts/${jurisdictionId}`,
      body,
    );

    return {
      id,
      ...body,
    };
  }

  @StoredRequest(PermittingActions.updateJurisdictionContact)
  async updateJurisdictionContact(
    contactId: string,
    body: Omit<JurisdictionDetailsContacts, 'id'>,
  ): Promise<JurisdictionDetailsContacts> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/contacts`,
      body,
      { params: { contactId } },
    );

    return {
      id: contactId,
      ...body,
    };
  }

  @StoredRequest(PermittingActions.removeJurisdictionContact)
  async removeJurisdictionContact(contactId: string): Promise<string> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions/contacts`,
      null,
      { params: { contactId } },
    );

    return contactId;
  }
}
